import React, { useState, useContext } from 'react';
import { AuthenticatedUserContext } from '../../auth';
import {
  Organization,
  AdminConfig,
  AgentConfig,
  AsanaConfig,
  FieldMapping,
  Portfolio,
} from '../../services/organizationService';
import { DEFAULT_AGENT_CONFIG } from '../../constants/defaultAgentConfig';
import { DEFAULT_FIELD_MAPPINGS, FIELD_TYPE_OPTIONS, FIELD_SOURCE_OPTIONS, PROMPT_INPUT_TYPE_OPTIONS } from '../../constants/defaultFieldMappings';
import { canEditOrgConfig } from '../../utils/adminAccess';
import { updateOrganizationS3 } from '../../services/organizationS3Service';
import './OrganizationSettings.css';

interface OrganizationSettingsProps {
  organization: Organization;
  onOrganizationUpdated: (updatedOrg: Organization) => void;
}

type SettingsTab = 'admins' | 'agents' | 'asana' | 'fieldmappings' | 'portfolios';

const OrganizationSettings: React.FC<OrganizationSettingsProps> = ({
  organization,
  onOrganizationUpdated,
}) => {
  const authenticatedUser = useContext(AuthenticatedUserContext);
  const userEmail = authenticatedUser?.email || '';
  const isOrgAdmin = canEditOrgConfig(userEmail, organization);

  const [activeSettingsTab, setActiveSettingsTab] = useState<SettingsTab>('admins');
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Admin config state
  const [adminConfig, setAdminConfig] = useState<AdminConfig>({
    primaryAdmin: organization.adminConfig?.primaryAdmin || userEmail,
    secondaryAdmin: organization.adminConfig?.secondaryAdmin || '',
  });

  // Agent config state
  const [agentConfig, setAgentConfig] = useState<AgentConfig>({
    chat: organization.agentConfig?.chat || DEFAULT_AGENT_CONFIG.chat,
    finaljson: organization.agentConfig?.finaljson || DEFAULT_AGENT_CONFIG.finaljson,
    explore: organization.agentConfig?.explore || DEFAULT_AGENT_CONFIG.explore,
    narrative: organization.agentConfig?.narrative || DEFAULT_AGENT_CONFIG.narrative,
    prioritize: organization.agentConfig?.prioritize || DEFAULT_AGENT_CONFIG.prioritize,
  });

  // Asana config state
  const [asanaConfig, setAsanaConfig] = useState<AsanaConfig>({
    personalAccessToken: organization.asanaConfig?.personalAccessToken || '',
    workspaceId: organization.asanaConfig?.workspaceId || '',
  });
  const [showPat, setShowPat] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'untested' | 'testing' | 'connected' | 'failed'>('untested');
  const [connectionUser, setConnectionUser] = useState<string>('');

  // Field mappings state
  const [fieldMappings, setFieldMappings] = useState<FieldMapping[]>(
    organization.fieldMappings && organization.fieldMappings.length > 0
      ? [...organization.fieldMappings]
      : [...DEFAULT_FIELD_MAPPINGS]
  );
  const [editingRowIndex, setEditingRowIndex] = useState<number | null>(null);
  const [editingRow, setEditingRow] = useState<FieldMapping | null>(null);
  const [selectedMappingRows, setSelectedMappingRows] = useState<Set<number>>(new Set());
  const [editingPromptIndex, setEditingPromptIndex] = useState<number | null>(null);

  // Portfolios state
  const [portfolios, setPortfolios] = useState<Portfolio[]>(
    organization.portfolios ? [...organization.portfolios] : []
  );
  const [showPortfolioModal, setShowPortfolioModal] = useState(false);
  const [editingPortfolio, setEditingPortfolio] = useState<Portfolio | null>(null);
  const [portfolioForm, setPortfolioForm] = useState({
    name: '',
    description: '',
    asanaProjectId: '',
    inheritConfig: true,
    primaryAdmin: userEmail,
    secondaryAdmin: '',
  });

  if (!isOrgAdmin) {
    return (
      <div className="settings-locked">
        <div className="settings-locked-icon">🔒</div>
        <h3>Settings Locked</h3>
        <p>Only organization admins can modify settings.</p>
        <p className="settings-locked-admin">
          Contact: {organization.adminConfig?.primaryAdmin || 'No admin configured'}
        </p>
      </div>
    );
  }

  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage(null);

    try {
      // Save PAT to Secrets Manager (not S3) if provided
      if (asanaConfig.personalAccessToken) {
        const apiUrl = process.env.REACT_APP_API_URL;
        const { apiKeyService } = await import('../../services/apiKeyService');
        const headers = await apiKeyService.getApiKeyHeaders();
        const credRes = await fetch(`${apiUrl}/api/asana/credentials`, {
          method: 'PUT',
          headers,
          body: JSON.stringify({
            accessToken: asanaConfig.personalAccessToken,
            workspaceId: asanaConfig.workspaceId || undefined,
            orgId: organization.id,
          }),
        });
        if (!credRes.ok) {
          throw new Error(`Failed to save credentials: ${credRes.status}`);
        }
      }

      const updates: Partial<Organization> = {
        adminConfig,
        agentConfig,
        asanaConfig: {
          // personalAccessToken is NOT saved to S3 — stored in Secrets Manager only
          workspaceId: asanaConfig.workspaceId || undefined,
        },
        fieldMappings,
        portfolios,
      };

      await updateOrganizationS3(organization.id, updates);

      // Update parent component state
      onOrganizationUpdated({
        ...organization,
        ...updates,
      });

      setSaveMessage({ type: 'success', text: 'Settings saved successfully!' });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      console.error('Error saving organization settings:', error);
      setSaveMessage({ type: 'error', text: `Failed to save: ${error}` });
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetAgents = () => {
    setAgentConfig({ ...DEFAULT_AGENT_CONFIG });
  };

  const handleTestConnection = async () => {
    if (!asanaConfig.personalAccessToken) {
      setConnectionStatus('failed');
      setConnectionUser('No PAT provided');
      return;
    }

    setConnectionStatus('testing');
    try {
      const response = await fetch('https://app.asana.com/api/1.0/users/me', {
        headers: {
          Authorization: `Bearer ${asanaConfig.personalAccessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setConnectionStatus('connected');
        setConnectionUser(data.data?.name || 'Unknown user');
      } else {
        setConnectionStatus('failed');
        setConnectionUser(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      setConnectionStatus('failed');
      setConnectionUser(`Connection error: ${error}`);
    }
  };

  return (
    <div className="org-settings">
      <div className="org-settings-header">
        <h3>⚙️ Organization Settings</h3>
        <div className="org-settings-actions">
          {saveMessage && (
            <span className={`save-message save-message-${saveMessage.type}`}>
              {saveMessage.type === 'success' ? '✅' : '❌'} {saveMessage.text}
            </span>
          )}
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : '💾 Save All Settings'}
          </button>
        </div>
      </div>

      <div className="settings-tabs">
        <button
          className={`settings-tab ${activeSettingsTab === 'admins' ? 'active' : ''}`}
          onClick={() => setActiveSettingsTab('admins')}
        >
          👤 Admins
        </button>
        <button
          className={`settings-tab ${activeSettingsTab === 'agents' ? 'active' : ''}`}
          onClick={() => setActiveSettingsTab('agents')}
        >
          🤖 Agents
        </button>
        <button
          className={`settings-tab ${activeSettingsTab === 'asana' ? 'active' : ''}`}
          onClick={() => setActiveSettingsTab('asana')}
        >
          🔗 Asana
        </button>
        <button
          className={`settings-tab ${activeSettingsTab === 'fieldmappings' ? 'active' : ''}`}
          onClick={() => setActiveSettingsTab('fieldmappings')}
        >
          🗂️ Field Mappings
        </button>
      </div>

      <div className="settings-content">
        {/* Admin Configuration */}
        {activeSettingsTab === 'admins' && (
          <div className="settings-panel">
            <h4>Admin Configuration</h4>
            <p className="settings-description">
              Only admins can modify organization settings, create portfolios, and manage field mappings.
            </p>

            <div className="settings-form-group">
              <label>Primary Admin (required)</label>
              <input
                type="email"
                value={adminConfig.primaryAdmin}
                onChange={(e) => setAdminConfig({ ...adminConfig, primaryAdmin: e.target.value })}
                placeholder="primary-admin@amazon.com"
                required
              />
            </div>

            <div className="settings-form-group">
              <label>Secondary Admin (optional)</label>
              <input
                type="email"
                value={adminConfig.secondaryAdmin || ''}
                onChange={(e) => setAdminConfig({ ...adminConfig, secondaryAdmin: e.target.value || undefined })}
                placeholder="secondary-admin@amazon.com"
              />
            </div>
          </div>
        )}

        {/* Agent Configuration */}
        {activeSettingsTab === 'agents' && (
          <div className="settings-panel">
            <div className="settings-panel-header">
              <h4>Agent Configuration</h4>
              <button className="btn btn-small" onClick={handleResetAgents}>
                🔄 Reset to Defaults
              </button>
            </div>
            <p className="settings-description">
              IRIS Agent IDs for the chatbot. Pre-filled with system defaults — only change if you have custom agents.
            </p>

            <div className="settings-form-group">
              <label>Chat Agent ID</label>
              <input
                type="text"
                value={agentConfig.chat}
                onChange={(e) => setAgentConfig({ ...agentConfig, chat: e.target.value })}
                placeholder="UUID for chat/intake agent"
              />
            </div>

            <div className="settings-form-group">
              <label>FinalJSON Agent ID</label>
              <input
                type="text"
                value={agentConfig.finaljson}
                onChange={(e) => setAgentConfig({ ...agentConfig, finaljson: e.target.value })}
                placeholder="UUID for JSON generation agent"
              />
            </div>

            <div className="settings-form-group">
              <label>Explore Agent ID</label>
              <input
                type="text"
                value={agentConfig.explore || ''}
                onChange={(e) => setAgentConfig({ ...agentConfig, explore: e.target.value || undefined })}
                placeholder="UUID for explore mode agent"
              />
            </div>

            <div className="settings-form-group">
              <label>Narrative Agent ID</label>
              <input
                type="text"
                value={agentConfig.narrative || ''}
                onChange={(e) => setAgentConfig({ ...agentConfig, narrative: e.target.value || undefined })}
                placeholder="UUID for narrative generation agent"
              />
            </div>

            <div className="settings-form-group">
              <label>Prioritize Agent ID</label>
              <input
                type="text"
                value={agentConfig.prioritize || ''}
                onChange={(e) => setAgentConfig({ ...agentConfig, prioritize: e.target.value || undefined })}
                placeholder="UUID for prioritization agent"
              />
            </div>
          </div>
        )}

        {/* Asana Credentials */}
        {activeSettingsTab === 'asana' && (
          <div className="settings-panel">
            <h4>Asana Credentials</h4>
            <p className="settings-description">
              Personal Access Token and Workspace ID for Asana integration. This PAT will be used for all portfolios that inherit organization settings.
            </p>

            <div className="settings-form-group">
              <label>Personal Access Token</label>
              <div className="pat-input-wrapper">
                <input
                  type={showPat ? 'text' : 'password'}
                  value={asanaConfig.personalAccessToken || ''}
                  onChange={(e) => {
                    setAsanaConfig({ ...asanaConfig, personalAccessToken: e.target.value });
                    setConnectionStatus('untested');
                  }}
                  placeholder="1/12345678:abcdef..."
                />
                <button
                  type="button"
                  className="pat-toggle-btn"
                  onClick={() => setShowPat(!showPat)}
                  title={showPat ? 'Hide PAT' : 'Show PAT'}
                >
                  {showPat ? '🙈' : '👁'}
                </button>
              </div>
            </div>

            <div className="settings-form-group">
              <label>Workspace ID</label>
              <input
                type="text"
                value={asanaConfig.workspaceId || ''}
                onChange={(e) => setAsanaConfig({ ...asanaConfig, workspaceId: e.target.value })}
                placeholder="12345678901234"
              />
            </div>

            <div className="connection-test">
              <button
                className="btn btn-secondary"
                onClick={handleTestConnection}
                disabled={connectionStatus === 'testing'}
              >
                {connectionStatus === 'testing' ? '⏳ Testing...' : '🔗 Test Connection'}
              </button>

              {connectionStatus === 'connected' && (
                <div className="connection-status connection-success">
                  ✅ Connected as "{connectionUser}"
                </div>
              )}
              {connectionStatus === 'failed' && (
                <div className="connection-status connection-error">
                  ❌ {connectionUser}
                </div>
              )}
              {connectionStatus === 'untested' && asanaConfig.personalAccessToken && (
                <div className="connection-status connection-untested">
                  ⚠️ PAT changed — click "Test Connection" to verify
                </div>
              )}
            </div>
          </div>
        )}
        {/* Field Mappings */}
        {activeSettingsTab === 'fieldmappings' && (
          <div className="settings-panel">
            <div className="settings-panel-header">
              <h4>Field Mappings ({fieldMappings.length}){selectedMappingRows.size > 0 && ` — ${selectedMappingRows.size} selected`}</h4>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button
                  className="btn btn-small"
                  onClick={() => {
                    if (window.confirm('Reset all mappings to system defaults? This will replace your current mappings.')) {
                      setFieldMappings([...DEFAULT_FIELD_MAPPINGS]);
                      setEditingRowIndex(null);
                      setEditingRow(null);
                    }
                  }}
                >
                  🔄 Reset to Defaults
                </button>
                <button
                  className="btn btn-small"
                  onClick={async () => {
                    // Find the first portfolio's asanaProjectId to import from
                    const projectId = portfolios[0]?.asanaProjectId;
                    const pat = asanaConfig.personalAccessToken;
                    if (!pat) {
                      alert('Please configure an Asana PAT in the Asana tab first.');
                      return;
                    }
                    if (!projectId) {
                      alert('Please create a portfolio with an Asana Project ID first.');
                      return;
                    }
                    try {
                      const resp = await fetch(
                        `https://app.asana.com/api/1.0/projects/${projectId}/custom_field_settings?opt_fields=custom_field.gid,custom_field.name,custom_field.type,custom_field.resource_subtype`,
                        { headers: { Authorization: `Bearer ${pat}` } }
                      );
                      if (!resp.ok) throw new Error(`Asana API ${resp.status}`);
                      const data = await resp.json();
                      const imported: FieldMapping[] = (data.data || []).map((setting: any) => {
                        const cf = setting.custom_field;
                        const typeMap: Record<string, FieldMapping['fieldType']> = {
                          text: 'text', number: 'number', enum: 'enum', multi_enum: 'multi_enum',
                          date: 'date', people: 'people',
                        };
                        return {
                          fieldName: cf.name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, ''),
                          asanaGid: cf.gid,
                          label: cf.name,
                          fieldType: typeMap[cf.resource_subtype || cf.type] || 'text',
                        };
                      });
                      if (imported.length === 0) {
                        alert('No custom fields found on this Asana project.');
                        return;
                      }
                      if (window.confirm(`Found ${imported.length} custom fields from Asana project ${projectId}. Replace current mappings?`)) {
                        setFieldMappings(imported);
                        setEditingRowIndex(null);
                        setEditingRow(null);
                      }
                    } catch (err) {
                      alert(`Failed to import from Asana: ${err}`);
                    }
                  }}
                >
                  📥 Import from Asana
                </button>
                {selectedMappingRows.size > 0 && (
                  <button
                    className="btn btn-small"
                    style={{ background: '#e53e3e', color: 'white' }}
                    onClick={() => {
                      if (window.confirm(`Delete ${selectedMappingRows.size} selected mapping${selectedMappingRows.size > 1 ? 's' : ''}?`)) {
                        setFieldMappings(fieldMappings.filter((_, i) => !selectedMappingRows.has(i)));
                        setSelectedMappingRows(new Set());
                        setEditingRowIndex(null);
                        setEditingRow(null);
                      }
                    }}
                  >
                    🗑 Delete Selected ({selectedMappingRows.size})
                  </button>
                )}
                <button
                  className="btn btn-small btn-primary"
                  onClick={() => {
                    const newMapping: FieldMapping = {
                      fieldName: '',
                      asanaGid: '',
                      label: '',
                      fieldType: 'text',
                    };
                    setFieldMappings([...fieldMappings, newMapping]);
                    setEditingRowIndex(fieldMappings.length);
                    setEditingRow({ ...newMapping });
                  }}
                >
                  + Add Mapping
                </button>
              </div>
            </div>
            <p className="settings-description">
              Map HeyFilly field names to Asana custom field GIDs. Both Field Name and Asana GID are editable.
              The Type dropdown determines how the field is processed when creating Asana tasks.
            </p>

            <div className="field-mappings-table-wrapper">
              <table className="field-mappings-table">
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}>
                      <input
                        type="checkbox"
                        checked={selectedMappingRows.size === fieldMappings.length && fieldMappings.length > 0}
                        onChange={() => {
                          if (selectedMappingRows.size === fieldMappings.length) {
                            setSelectedMappingRows(new Set());
                          } else {
                            setSelectedMappingRows(new Set(fieldMappings.map((_, i) => i)));
                          }
                        }}
                        title={selectedMappingRows.size === fieldMappings.length ? 'Deselect all' : 'Select all'}
                      />
                    </th>
                    <th>Field Name</th>
                    <th>Label</th>
                    <th>Asana GID</th>
                    <th>Type</th>
                    <th>Source</th>
                    <th style={{ width: '50px' }}>Write?</th>
                    <th style={{ width: '100px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {fieldMappings.map((mapping, index) => (
                    <tr key={index} className={`${editingRowIndex === index ? 'editing-row' : ''} ${selectedMappingRows.has(index) ? 'selected-row' : ''}`}>
                      <td style={{ width: '40px' }}>
                        <input
                          type="checkbox"
                          checked={selectedMappingRows.has(index)}
                          onChange={() => {
                            const newSet = new Set(selectedMappingRows);
                            if (newSet.has(index)) newSet.delete(index);
                            else newSet.add(index);
                            setSelectedMappingRows(newSet);
                          }}
                        />
                      </td>
                      {editingRowIndex === index && editingRow ? (
                        <>
                          <td>
                            <input
                              type="text"
                              value={editingRow.fieldName}
                              onChange={(e) => setEditingRow({ ...editingRow, fieldName: e.target.value })}
                              placeholder="field_name"
                              className="fm-input"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={editingRow.label || ''}
                              onChange={(e) => setEditingRow({ ...editingRow, label: e.target.value })}
                              placeholder="Display Label"
                              className="fm-input"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={editingRow.asanaGid}
                              onChange={(e) => setEditingRow({ ...editingRow, asanaGid: e.target.value })}
                              placeholder="1234567890123"
                              className="fm-input fm-input-mono"
                            />
                          </td>
                          <td>
                            <select
                              value={editingRow.fieldType || 'text'}
                              onChange={(e) => setEditingRow({ ...editingRow, fieldType: e.target.value as FieldMapping['fieldType'] })}
                              className="fm-select"
                            >
                              {FIELD_TYPE_OPTIONS.map((type) => (
                                <option key={type} value={type}>{type}</option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <select
                              value={editingRow.source || 'iris'}
                              onChange={(e) => setEditingRow({ ...editingRow, source: e.target.value as FieldMapping['source'] })}
                              className="fm-select"
                            >
                              {FIELD_SOURCE_OPTIONS.map((s) => (
                                <option key={s} value={s}>{s}</option>
                              ))}
                            </select>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={editingRow.writeToAsana !== false}
                              onChange={(e) => setEditingRow({ ...editingRow, writeToAsana: e.target.checked })}
                            />
                          </td>
                          <td>
                            <div className="fm-actions">
                              <button
                                className="fm-btn fm-btn-save"
                                onClick={() => {
                                  if (!editingRow.fieldName.trim() || !editingRow.asanaGid.trim()) {
                                    alert('Field Name and Asana GID are required.');
                                    return;
                                  }
                                  const updated = [...fieldMappings];
                                  updated[index] = { ...editingRow };
                                  setFieldMappings(updated);
                                  setEditingRowIndex(null);
                                  setEditingRow(null);
                                }}
                                title="Save"
                              >
                                ✅
                              </button>
                              <button
                                className="fm-btn fm-btn-cancel"
                                onClick={() => {
                                  // If it was a new empty row, remove it
                                  if (!mapping.fieldName && !mapping.asanaGid) {
                                    setFieldMappings(fieldMappings.filter((_, i) => i !== index));
                                  }
                                  setEditingRowIndex(null);
                                  setEditingRow(null);
                                }}
                                title="Cancel"
                              >
                                ❌
                              </button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="fm-cell-name">{mapping.fieldName}</td>
                          <td className="fm-cell-label">{mapping.label || '—'}</td>
                          <td className="fm-cell-gid">{mapping.asanaGid}</td>
                          <td>
                            <span className={`fm-type-badge fm-type-${mapping.fieldType || 'text'}`}>
                              {mapping.fieldType || 'text'}
                            </span>
                          </td>
                          <td>
                            <span className={`fm-type-badge fm-type-${mapping.source || 'iris'}`}>
                              {mapping.source === 'iris' ? '🤖' : mapping.source === 'prompt' ? '💬' : mapping.source === 'system' ? '⚙️' : '📝'} {mapping.source || 'iris'}
                            </span>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {mapping.writeToAsana !== false ? '✅' : '❌'}
                          </td>
                          <td>
                            <div className="fm-actions">
                              <button
                                className="fm-btn"
                                onClick={() => {
                                  setEditingRowIndex(index);
                                  setEditingRow({ ...mapping });
                                }}
                                title="Edit"
                              >
                                ✏️
                              </button>
                              {mapping.source === 'prompt' && (
                                <button
                                  className="fm-btn"
                                  onClick={() => setEditingPromptIndex(index)}
                                  title="Configure prompt"
                                >
                                  ⚙️
                                </button>
                              )}
                              <button
                                className="fm-btn fm-btn-delete"
                                onClick={() => {
                                  if (window.confirm(`Delete mapping "${mapping.fieldName}"?`)) {
                                    setFieldMappings(fieldMappings.filter((_, i) => i !== index));
                                  }
                                }}
                                title="Delete"
                              >
                                🗑
                              </button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          {/* Prompt Config Modal */}
          {editingPromptIndex !== null && (
            <div className="modal" onClick={() => setEditingPromptIndex(null)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>Configure Prompt: {fieldMappings[editingPromptIndex]?.label || fieldMappings[editingPromptIndex]?.fieldName}</h3>
                  <button className="close-btn" onClick={() => setEditingPromptIndex(null)}>×</button>
                </div>
                <div style={{ padding: '16px' }}>
                  <div className="settings-form-group">
                    <label>Question</label>
                    <input
                      type="text"
                      value={fieldMappings[editingPromptIndex]?.promptConfig?.question || ''}
                      onChange={(e) => {
                        const updated = [...fieldMappings];
                        updated[editingPromptIndex!] = {
                          ...updated[editingPromptIndex!],
                          promptConfig: { ...updated[editingPromptIndex!].promptConfig!, question: e.target.value }
                        };
                        setFieldMappings(updated);
                      }}
                      placeholder="What question to ask the user?"
                    />
                  </div>
                  <div className="settings-form-group">
                    <label>Input Type</label>
                    <select
                      value={fieldMappings[editingPromptIndex]?.promptConfig?.inputType || 'chips'}
                      onChange={(e) => {
                        const updated = [...fieldMappings];
                        updated[editingPromptIndex!] = {
                          ...updated[editingPromptIndex!],
                          promptConfig: { ...updated[editingPromptIndex!].promptConfig!, inputType: e.target.value as any }
                        };
                        setFieldMappings(updated);
                      }}
                    >
                      {PROMPT_INPUT_TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  {(fieldMappings[editingPromptIndex]?.promptConfig?.inputType === 'chips' || fieldMappings[editingPromptIndex]?.promptConfig?.inputType === 'dropdown') && (
                    <div className="settings-form-group">
                      <label>Options (comma-separated)</label>
                      <input
                        type="text"
                        value={(fieldMappings[editingPromptIndex]?.promptConfig?.options || []).join(', ')}
                        onChange={(e) => {
                          const updated = [...fieldMappings];
                          updated[editingPromptIndex!] = {
                            ...updated[editingPromptIndex!],
                            promptConfig: { ...updated[editingPromptIndex!].promptConfig!, options: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }
                          };
                          setFieldMappings(updated);
                        }}
                        placeholder="Option 1, Option 2, Option 3"
                      />
                    </div>
                  )}
                  <div className="settings-form-group">
                    <label>Order (position in structured capture flow)</label>
                    <input
                      type="number"
                      value={fieldMappings[editingPromptIndex]?.promptConfig?.order || 1}
                      onChange={(e) => {
                        const updated = [...fieldMappings];
                        updated[editingPromptIndex!] = {
                          ...updated[editingPromptIndex!],
                          promptConfig: { ...updated[editingPromptIndex!].promptConfig!, order: parseInt(e.target.value) || 1 }
                        };
                        setFieldMappings(updated);
                      }}
                      min={1}
                    />
                  </div>
                  <div className="settings-form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={fieldMappings[editingPromptIndex]?.promptConfig?.required !== false}
                        onChange={(e) => {
                          const updated = [...fieldMappings];
                          updated[editingPromptIndex!] = {
                            ...updated[editingPromptIndex!],
                            promptConfig: { ...updated[editingPromptIndex!].promptConfig!, required: e.target.checked }
                          };
                          setFieldMappings(updated);
                        }}
                      />
                      <span>Required</span>
                    </label>
                  </div>
                  <div className="form-actions">
                    <button className="btn btn-primary" onClick={() => setEditingPromptIndex(null)}>Done</button>
                  </div>
                </div>
              </div>
            </div>
          )}
          </div>
        )}

        {/* Portfolios */}
        {activeSettingsTab === 'portfolios' && (
          <div className="settings-panel">
            <div className="settings-panel-header">
              <h4>Portfolios</h4>
              <button
                className="btn btn-small btn-primary"
                onClick={() => {
                  setEditingPortfolio(null);
                  setPortfolioForm({
                    name: '',
                    description: '',
                    asanaProjectId: '',
                    inheritConfig: true,
                    primaryAdmin: userEmail,
                    secondaryAdmin: '',
                  });
                  setShowPortfolioModal(true);
                }}
              >
                + Add Portfolio
              </button>
            </div>
            <p className="settings-description">
              Each portfolio maps to an Asana project and contains its own directors, charter, mission, and priorities.
              Portfolios can inherit org-level config (agents, PAT, field mappings) or use their own.
            </p>

            {portfolios.length === 0 ? (
              <div className="portfolio-empty">
                <p>📁 No portfolios yet. Create one to start using the chatbot.</p>
              </div>
            ) : (
              <div className="portfolio-list">
                {portfolios.map((portfolio) => (
                  <div key={portfolio.id} className="portfolio-card">
                    <div className="portfolio-card-header">
                      <div className="portfolio-card-info">
                        <h5>{portfolio.name}</h5>
                        {portfolio.description && (
                          <p className="portfolio-card-desc">{portfolio.description}</p>
                        )}
                      </div>
                      <div className="portfolio-card-actions">
                        <button
                          className="fm-btn"
                          onClick={() => {
                            setEditingPortfolio(portfolio);
                            setPortfolioForm({
                              name: portfolio.name,
                              description: portfolio.description || '',
                              asanaProjectId: portfolio.asanaProjectId,
                              inheritConfig: portfolio.inheritConfig,
                              primaryAdmin: portfolio.adminConfig?.primaryAdmin || userEmail,
                              secondaryAdmin: portfolio.adminConfig?.secondaryAdmin || '',
                            });
                            setShowPortfolioModal(true);
                          }}
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button
                          className="fm-btn fm-btn-delete"
                          onClick={() => {
                            if (window.confirm(`Delete portfolio "${portfolio.name}"? This cannot be undone.`)) {
                              setPortfolios(portfolios.filter((p) => p.id !== portfolio.id));
                            }
                          }}
                          title="Delete"
                        >
                          🗑
                        </button>
                      </div>
                    </div>
                    <div className="portfolio-card-meta">
                      <span className="portfolio-meta-item">
                        🔗 Asana: <code>{portfolio.asanaProjectId}</code>
                      </span>
                      <span className={`portfolio-inherit-badge ${portfolio.inheritConfig ? 'inherit-yes' : 'inherit-no'}`}>
                        {portfolio.inheritConfig ? '🔗 Inherits Org Config' : '⚙️ Custom Config'}
                      </span>
                      <span className="portfolio-meta-item">
                        👥 {portfolio.directors?.length || 0} directors
                      </span>
                      {portfolio.adminConfig?.primaryAdmin && (
                        <span className="portfolio-meta-item">
                          👤 {portfolio.adminConfig.primaryAdmin}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Portfolio Create/Edit Modal */}
      {showPortfolioModal && (
        <div className="modal" onClick={() => setShowPortfolioModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingPortfolio ? 'Edit Portfolio' : 'Create Portfolio'}</h3>
              <button className="close-btn" onClick={() => setShowPortfolioModal(false)}>×</button>
            </div>
            <div className="portfolio-form">
              <div className="settings-form-group">
                <label>Portfolio Name *</label>
                <input
                  type="text"
                  value={portfolioForm.name}
                  onChange={(e) => setPortfolioForm({ ...portfolioForm, name: e.target.value })}
                  placeholder="e.g., AFT Outbound"
                  required
                />
              </div>

              <div className="settings-form-group">
                <label>Description</label>
                <input
                  type="text"
                  value={portfolioForm.description}
                  onChange={(e) => setPortfolioForm({ ...portfolioForm, description: e.target.value })}
                  placeholder="Brief description of this portfolio"
                />
              </div>

              <div className="settings-form-group">
                <label>Asana Project ID *</label>
                <input
                  type="text"
                  value={portfolioForm.asanaProjectId}
                  onChange={(e) => setPortfolioForm({ ...portfolioForm, asanaProjectId: e.target.value })}
                  placeholder="1234567890123"
                  required
                  style={{ fontFamily: "'SF Mono', 'Fira Code', monospace" }}
                />
              </div>

              <div className="settings-form-group">
                <label>Primary Admin</label>
                <input
                  type="email"
                  value={portfolioForm.primaryAdmin}
                  onChange={(e) => setPortfolioForm({ ...portfolioForm, primaryAdmin: e.target.value })}
                  placeholder="admin@amazon.com"
                />
              </div>

              <div className="settings-form-group">
                <label>Secondary Admin</label>
                <input
                  type="email"
                  value={portfolioForm.secondaryAdmin}
                  onChange={(e) => setPortfolioForm({ ...portfolioForm, secondaryAdmin: e.target.value })}
                  placeholder="backup-admin@amazon.com"
                />
              </div>

              <div className="settings-form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={portfolioForm.inheritConfig}
                    onChange={(e) => setPortfolioForm({ ...portfolioForm, inheritConfig: e.target.checked })}
                  />
                  <span>Inherit organization settings</span>
                </label>
                <p className="settings-description" style={{ marginTop: '4px', marginBottom: '0' }}>
                  {portfolioForm.inheritConfig
                    ? 'Uses org-level agent IDs, Asana PAT, and field mappings. Only the Asana Project ID is unique to this portfolio.'
                    : 'This portfolio uses its own configuration. Set custom values below.'}
                </p>
              </div>

              {/* Custom config sections — shown when inheritance is OFF */}
              {!portfolioForm.inheritConfig && (
                <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '16px', marginTop: '8px' }}>
                  <h4 style={{ fontSize: '14px', color: '#4a5568', marginBottom: '12px' }}>⚙️ Custom Portfolio Configuration</h4>
                  
                  <p className="settings-description" style={{ marginBottom: '12px' }}>
                    These override the org-level settings for this portfolio only. Leave blank to fall back to org defaults.
                  </p>

                  <details style={{ marginBottom: '12px' }}>
                    <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: '13px', color: '#4a5568', padding: '8px 0' }}>
                      🤖 Custom Agent IDs
                    </summary>
                    <div style={{ padding: '8px 0 8px 16px' }}>
                      <div className="settings-form-group">
                        <label>Chat Agent ID</label>
                        <input type="text" placeholder="Leave blank to use org default" style={{ fontFamily: "'SF Mono', monospace", fontSize: '13px' }} />
                      </div>
                      <div className="settings-form-group">
                        <label>FinalJSON Agent ID</label>
                        <input type="text" placeholder="Leave blank to use org default" style={{ fontFamily: "'SF Mono', monospace", fontSize: '13px' }} />
                      </div>
                    </div>
                  </details>

                  <details style={{ marginBottom: '12px' }}>
                    <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: '13px', color: '#4a5568', padding: '8px 0' }}>
                      🔗 Custom Asana Credentials
                    </summary>
                    <div style={{ padding: '8px 0 8px 16px' }}>
                      <div className="settings-form-group">
                        <label>Personal Access Token</label>
                        <input type="password" placeholder="Leave blank to use org PAT" />
                      </div>
                      <div className="settings-form-group">
                        <label>Workspace ID</label>
                        <input type="text" placeholder="Leave blank to use org workspace" />
                      </div>
                    </div>
                  </details>

                  <details style={{ marginBottom: '12px' }}>
                    <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: '13px', color: '#4a5568', padding: '8px 0' }}>
                      🗂️ Custom Field Mappings
                    </summary>
                    <div style={{ padding: '8px 0 8px 16px' }}>
                      <p className="settings-description">
                        Portfolio-level field mapping overrides will be available in a future update. 
                        For now, this portfolio will fall back to org-level field mappings.
                      </p>
                    </div>
                  </details>
                </div>
              )}

              <div className="form-actions" style={{ marginTop: '20px' }}>
                <button className="btn" onClick={() => setShowPortfolioModal(false)}>
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    if (!portfolioForm.name.trim() || !portfolioForm.asanaProjectId.trim()) {
                      alert('Portfolio Name and Asana Project ID are required.');
                      return;
                    }

                    const now = new Date().toISOString();

                    let updatedPortfolios: Portfolio[];
                    
                    if (editingPortfolio) {
                      // Update existing portfolio
                      updatedPortfolios = portfolios.map((p) =>
                        p.id === editingPortfolio.id
                          ? {
                              ...p,
                              name: portfolioForm.name,
                              description: portfolioForm.description || undefined,
                              asanaProjectId: portfolioForm.asanaProjectId,
                              inheritConfig: portfolioForm.inheritConfig,
                              adminConfig: {
                                primaryAdmin: portfolioForm.primaryAdmin,
                                secondaryAdmin: portfolioForm.secondaryAdmin || undefined,
                              },
                              updatedAt: now,
                            }
                          : p
                      );
                    } else {
                      // Create new portfolio
                      const newPortfolio: Portfolio = {
                        id: `portfolio-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
                        name: portfolioForm.name,
                        description: portfolioForm.description || undefined,
                        asanaProjectId: portfolioForm.asanaProjectId,
                        inheritConfig: portfolioForm.inheritConfig,
                        adminConfig: {
                          primaryAdmin: portfolioForm.primaryAdmin,
                          secondaryAdmin: portfolioForm.secondaryAdmin || undefined,
                        },
                        directors: [],
                        createdAt: now,
                        updatedAt: now,
                        createdBy: userEmail,
                      };
                      updatedPortfolios = [...portfolios, newPortfolio];
                    }
                    
                    setPortfolios(updatedPortfolios);
                    setShowPortfolioModal(false);
                    setEditingPortfolio(null);
                    
                    // Auto-save portfolios to S3 immediately
                    (async () => {
                      try {
                        await updateOrganizationS3(organization.id, { portfolios: updatedPortfolios });
                        onOrganizationUpdated({ ...organization, portfolios: updatedPortfolios });
                        setSaveMessage({ type: 'success', text: 'Portfolio saved!' });
                        setTimeout(() => setSaveMessage(null), 3000);
                      } catch (err) {
                        console.error('Error auto-saving portfolio:', err);
                        setSaveMessage({ type: 'error', text: `Failed to save portfolio: ${err}` });
                      }
                    })();
                  }}
                >
                  {editingPortfolio ? 'Save Changes' : 'Create Portfolio'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationSettings;
